import { faMinusCircle, faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldArray, useFormikContext } from 'formik';
import type { FC } from 'react';
import { useEffect } from 'react';
import { yesNoOptions } from 'src/apis/storage/mappings';
import type { MarketArea } from 'src/apis/storage/types';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { Editor } from 'src/components/form/editor';
import { formGapClasses } from 'src/components/form/form-container';
import type { FormOption } from 'src/components/form/form-option';
import { Input } from 'src/components/form/input';
import { Radios } from 'src/components/form/radios';
import { NetworkPointMultiSelect } from 'src/components/form/select/network-point-select/network-point-multi-select';
import { SearchableSingleSelect } from 'src/components/form/select/searchable-single-select';
import { Spacer } from 'src/components/layout/spacer';
import { Heading } from 'src/components/text/heading';
import { useClientSearch } from 'src/hooks/use-client-search';
import type { CreateStorageLocationFormValues } from 'src/pages/storage/locations-new/create/schema';

type Props = {
  marketAreas: MarketArea[];
};

export const CreateStorageLocationFormFields: FC<Props> = ({ marketAreas }) => {
  const { values } = useFormikContext<CreateStorageLocationFormValues>();

  const marketAreaOptions = marketAreas.map((ma) => ({
    label: ma.name,
    value: ma.id,
  }));

  return (
    <>
      <Heading data-testid="identifier-section" mode="title" size="md">
        Identifier
      </Heading>
      <Input
        name="locationName"
        label="Location Name"
        placeholder="E.g. North Storage Zone 3"
        markAsRequired
      />
      <Input
        name="locationEic"
        label="Location EIC"
        placeholder="E.g. 21XSTORAGE0001L"
        markAsRequired
      />
      <Spacer />

      <Heading data-testid="market-section" mode="title" size="sm">
        Market
      </Heading>

      <FieldArray
        name="marketAreas"
        render={(arrayHelpers) => (
          <div
            className={`
              grid
              ${formGapClasses}
            `}
          >
            {values.marketAreas.map((_, index) => (
              <MarketAreaRow
                key={index}
                index={index}
                marketAreaSelected={Boolean(
                  values.marketAreas[index]?.marketAreaId?.value
                )}
                onRemove={() => arrayHelpers.remove(index)}
                options={marketAreaOptions}
              />
            ))}
            <div className="flex justify-end">
              <Button
                mode="link"
                onClick={() =>
                  arrayHelpers.push({
                    marketAreaId: null,
                    injectionPoints: [],
                    withdrawalPoints: [],
                  })
                }
                data-testid="add-market-area"
              >
                <FontAwesomeIcon icon={faPlusCircle} className="mr-xs" />
                Add Market Area
              </Button>
            </div>
          </div>
        )}
      />

      <Heading data-testid="virtual-point-section" mode="title" size="sm">
        Virtual Point
      </Heading>
      <Radios
        name="virtualPoint"
        label="Virtual Point"
        options={yesNoOptions}
      />
      <Spacer />

      <Heading data-testid="general-information-section" mode="title" size="sm">
        General Information
      </Heading>
      <Editor name="comment" label="Comments" />
      <Spacer />
      <Input name="link" label="Link" placeholder="E.g. https://example.com" />
    </>
  );
};

const MarketAreaRow: FC<{
  index: number;
  marketAreaSelected: boolean;
  onRemove: () => void;
  options: FormOption<string>[];
}> = ({ index, marketAreaSelected, onRemove, options }) => {
  const { setFieldValue } = useFormikContext<CreateStorageLocationFormValues>();
  const marketAreasSearch = useClientSearch(options, (opt, search) =>
    opt.label.toLowerCase().match(search)
  );

  useEffect(() => {
    if (!marketAreaSelected) {
      setFieldValue(`marketAreas[${index}].injectionPoints`, []);
      setFieldValue(`marketAreas[${index}].withdrawalPoints`, []);
    }
  }, [marketAreaSelected, index, setFieldValue]);

  return (
    <Card mode="default" data-testid={`market-area-row-${index}`}>
      <div className="grid grid-flow-col justify-between">
        <Heading mode="title" size="md">
          Market Area {index + 1}
        </Heading>

        {index > 0 && (
          <Button
            mode="link"
            onClick={onRemove}
            data-testid={`remove-market-area-${index}`}
          >
            <FontAwesomeIcon icon={faMinusCircle} /> Remove
          </Button>
        )}
      </div>

      <div
        className={`
          mt-md grid gap-y-md
          ${formGapClasses}
        `}
      >
        <SearchableSingleSelect
          name={`marketAreas[${index}].marketAreaId`}
          label="Market Area"
          placeholder="E.g. Market Area East"
          markAsRequired
          {...marketAreasSearch}
        />
        {marketAreaSelected && (
          <>
            <NetworkPointMultiSelect
              name={`marketAreas[${index}].injectionPoints`}
              label="List of Injection Points"
              placeholder="Search for injection points"
              direction="EXIT"
            />
            <NetworkPointMultiSelect
              name={`marketAreas[${index}].withdrawalPoints`}
              label="List of Withdrawal Points"
              placeholder="Search for withdrawal points"
              direction="ENTRY"
            />
          </>
        )}
      </div>
    </Card>
  );
};
